import { ENABLE_CODE_PREVIEW } from '@core/constant/featureFlags';
import { fireAndForget } from '@core/util/fireAndForget';
import { throwOnErr } from '@core/util/result';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/solid-query';
import { type Accessor, onCleanup } from 'solid-js';
import { previewServiceClient } from '../../service-clients/service-preview/client';
import { subscribeSocketSessionStarted } from './queue-sync';

const listeners = new Set<(sessionId: string) => void>();
/** Paired with preview_gateway's agent_session_preview invalidation event. */
export function handleAgentSessionPreview(event: { agentSessionId: string }) {
  for (const listener of listeners) listener(event.agentSessionId);
}
const key = (sessionId: string | undefined) =>
  ['agentSession', 'preview', sessionId ?? ''] as const;

function agentPreviewQueryOptions(sessionId: string | undefined) {
  return queryOptions({
    queryKey: key(sessionId),
    queryFn: () => throwOnErr(() => previewServiceClient.get(sessionId!)),
    enabled: ENABLE_CODE_PREVIEW && Boolean(sessionId),
    staleTime: 0,
    // Repairs missed events and renews the viewer's event subscription.
    refetchInterval: 30_000,
    retry: false,
  });
}

export function useAgentPreview(sessionId: Accessor<string | undefined>) {
  const client = useQueryClient();
  const invalidate = () =>
    client.invalidateQueries({ queryKey: key(sessionId()) });
  const listener = (id: string) => {
    if (id === sessionId()) fireAndForget(invalidate(), 'agent-preview');
  };
  listeners.add(listener);
  onCleanup(() => listeners.delete(listener));
  onCleanup(
    subscribeSocketSessionStarted(() =>
      fireAndForget(invalidate(), 'agent-preview')
    )
  );
  const query = useQuery(() => agentPreviewQueryOptions(sessionId()));
  return {
    available: ENABLE_CODE_PREVIEW,
    query,
    open: () => {
      if (!ENABLE_CODE_PREVIEW) throw new Error('Code preview is unavailable');
      return throwOnErr(() => previewServiceClient.open(sessionId()!));
    },
    stop: async () => {
      if (!ENABLE_CODE_PREVIEW) throw new Error('Code preview is unavailable');
      await throwOnErr(() => previewServiceClient.stop(sessionId()!));
      await invalidate();
    },
  };
}
